import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Alert, Box,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import InvButton from './InvButton';
import { InvTextField } from './InvField';
import { IPD_COLORS, cardSx } from '../theme';

/**
 * Pharmacist soft-gate modal — captures license + confirmation before
 * dispensing controlled / narcotic medicines.
 */
export default function ControlledDrugAuthModal({
  open,
  onClose,
  onConfirm,
  loading = false,
  lineCount = 1,
  requiresSupervisorApproval = true,
}) {
  const [licenseNumber, setLicenseNumber] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  function handleClose() {
    if (loading) return;
    setLicenseNumber('');
    setConfirmed(false);
    onClose?.();
  }

  function handleConfirm() {
    onConfirm?.({ licenseNumber: licenseNumber.trim(), confirmed });
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <LockIcon sx={{ color: IPD_COLORS.warning.text, fontSize: 22 }} />
        Controlled Medicine Authorization
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: IPD_COLORS.textSecondary, mb: 2 }}>
          {lineCount === 1
            ? 'This medicine is a controlled or narcotic substance.'
            : `${lineCount} medicines are controlled or narcotic substances.`}
          {' '}Pharmacist authorization is required before dispensing.
        </Typography>

        {requiresSupervisorApproval && (
          <Alert severity="warning" sx={{ borderRadius: '10px', mb: 2 }}>
            Staged IPD dispense also requires supervisor approval in the Controlled Queue
            before stock can be reserved.
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <InvTextField
            label="Pharmacist license number"
            value={licenseNumber}
            onChange={(e) => setLicenseNumber(e.target.value)}
            placeholder="e.g. PH-12345"
            fullWidth
          />
        </Box>

        <Box
          component="label"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            cursor: 'pointer',
            fontSize: 13,
            color: IPD_COLORS.textPrimary,
          }}
        >
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          I confirm the prescription is valid and I am authorized to dispense controlled medicines.
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <InvButton variant="outlined" tone="teal" onClick={handleClose} disabled={loading}>
          Cancel
        </InvButton>
        <InvButton loading={loading} onClick={handleConfirm} disabled={!confirmed}>
          Authorize & Continue
        </InvButton>
      </DialogActions>
    </Dialog>
  );
}
